/**
 * CoordinateHelper - Converts between robot and Three.js coordinate systems
 * 
 * Robot coordinates: X=right, Y=forward, Z=up
 * Three.js coordinates: X=right, Y=up, Z=-forward
 */
import * as THREE from 'three';

export class CoordinateHelper {
  /**
   * Convert robot position to Three.js position
   * @param {Object} robotPos - Robot position {x, y, z}
   * @returns {THREE.Vector3} Three.js position
   */
  static robotToThreeJS(robotPos) {
    return new THREE.Vector3(
      robotPos.x,      // Robot X → Three.js X
      robotPos.z,      // Robot Z → Three.js Y (up)
      -robotPos.y      // Robot Y → Three.js -Z
    );
  }

  /**
   * Convert Three.js position to robot position
   * @param {THREE.Vector3} threePos - Three.js position
   * @returns {Object} Robot position {x, y, z}
   */
  static threeJSToRobot(threePos) {
    return {
      x: threePos.x,    // Three.js X → Robot X
      y: -threePos.z,   // Three.js -Z → Robot Y
      z: threePos.y     // Three.js Y → Robot Z (up)
    };
  }

  /**
   * Convert robot axis-angle rotation to Three.js quaternion
   * @param {Object} robotRot - Robot rotation {rx, ry, rz} in axis-angle representation
   * @returns {THREE.Quaternion} Three.js quaternion
   */
  static robotRotationToQuaternion(robotRot) {
    const rx = robotRot.rx || 0;
    const ry = robotRot.ry || 0;
    const rz = robotRot.rz || 0;

    // Transform axis-angle from robot coords to Three.js coords
    const threejsRX = rx;      // X stays X
    const threejsRY = rz;      // Robot Z (up) -> Three.js Y (up)
    const threejsRZ = -ry;     // Robot Y (forward) -> Three.js -Z

    // Convert axis-angle to quaternion
    const angle = Math.sqrt(threejsRX * threejsRX + threejsRY * threejsRY + threejsRZ * threejsRZ);
    const quaternion = new THREE.Quaternion();
    
    if (angle > 1e-10) {
      const axis = new THREE.Vector3(threejsRX / angle, threejsRY / angle, threejsRZ / angle);
      quaternion.setFromAxisAngle(axis, angle);
    }
    
    return quaternion;
  }

  /**
   * Apply robot rotation to a Three.js Object3D
   * @param {THREE.Object3D} object - Object to rotate
   * @param {Object} robotRot - Robot rotation {rx, ry, rz}
   */
  static applyRobotRotation(object, robotRot) {
    if (!robotRot) return;
    
    const quaternion = this.robotRotationToQuaternion(robotRot);
    object.setRotationFromQuaternion(quaternion);
  }
}
